import styled from '@emotion/styled';
import React, { useRef } from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import videojs, { VideoJsPlayer } from 'video.js';
import 'video.js/dist/video-js.css';

// See https://www.notion.so/modernhealth/Web-Video-Spike-19b9d00b96784605990ff91147d9de02
// for reasoning behind choosing video.js as the new VideoPlayer solution.

interface VideoJsSource {
    src: string;
    type: string;
}

interface VideoJSTextTrack {
    kind: VideoJsTextTrackKind;
    label: string;
    srcLang: string;
    src: string;
}

interface VideoPlayerProps {
    source: VideoJsSource;
    poster: string;
    tracks: readonly VideoJSTextTrack[];
}

export enum VideoJsTextTrackKind {
    Subtitles = 'subtitles',
    Captions = 'captions',
    Descriptions = 'descriptions',
    Chapters = 'chapters',
    Metadata = 'metadata',
}

export enum VideoPlayerSupportedFileFormats {
    MP4 = 'video/mp4',
}

const PLAYER_BORDER_RADIUS_REM = pxToRem(6);
const PLAY_BUTTON_RADIUS_REM = 1.75;

const StyledVideo = styled.video({
    '.vjs-control-bar': {
        borderBottomLeftRadius: PLAYER_BORDER_RADIUS_REM,
        borderBottomRightRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.video-js.mh-player': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-tech': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-poster': {
        borderRadius: PLAYER_BORDER_RADIUS_REM,
    },
    '.vjs-big-play-button.round-white': {
        border: 'none',
        backgroundColor: 'white',
        borderRadius: '50%',
        height: `${2 * PLAY_BUTTON_RADIUS_REM}rem`,
        width: `${2 * PLAY_BUTTON_RADIUS_REM}rem`,
        marginLeft: `${-PLAY_BUTTON_RADIUS_REM}rem`,
        marginTop: `${(-PLAY_BUTTON_RADIUS_REM / 4) * 3}rem`,
    },
    '.vjs-big-play-button .vjs-icon-placeholder:before': {
        color: 'black',
        marginTop: `${PLAY_BUTTON_RADIUS_REM / 4}rem`,
    },
    // https://docs.videojs.com/tutorial-text-tracks.html#text-track-settings
    '.vjs-texttrack-settings': {
        display: 'none',
    },
    borderRadius: PLAYER_BORDER_RADIUS_REM,
    // backgroundColor of the videoPlayer is black by default; with our borderRadius styling,
    // the background peeks out slightly and causes a noticeable shadow behind the player
    // We use `inherit` here rather than white since it is more likely to support default
    // as well as "dark mode" browser variants.
    backgroundColor: 'inherit',
});

export const VideoPlayer: React.FC<VideoPlayerProps> = (props: VideoPlayerProps) => {
    const { source, poster, tracks } = props;
    const options = useMemo(() => {
        return {
            sources: [source],
            poster,
            controls: true,
            fluid: true,
            tracks: [...tracks],
            controlBar: {
                volumePanel: {
                    inline: false,
                },
                pictureInPictureToggle: false,
            },
        };
    }, [poster, source, tracks]);
    const playerRef = useRef<VideoJsPlayer | null>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const videoRef = useCallback(
        (element: HTMLVideoElement | null) => {
            videoElementRef.current = element;
            if (!element) return;
            const player = videojs(element, options, () => {
                // https://docs.videojs.com/tutorial-skins.html#add-a-custom-class-to-the-player
                const bigPlayButton = player.getChild('bigPlayButton');
                bigPlayButton?.addClass('round-white');
            });
        },
        [options],
    );

    React.useEffect(() => {
        if (videoElementRef.current) {
            videoRef(videoElementRef.current);
        }

        return () => {
            playerRef.current?.dispose();
            playerRef.current = null;
        };
    }, []);

    return (
        <div vjs-data-element>
            <StyledVideo ref={videoElementRef} className="video-js vjs-big-play-centered">
                {tracks.map(track => (
                    <track key={track.src} {...track} />
                ))}
            </StyledVideo>
        </div>
    );
};
export default VideoPlayer;
